/**
 * refer: https://github.com/backstage/backstage/blob/master/plugins/catalog-import/src/index.ts
 */
import { InfoCard, Link, SupportButton } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { Box, Container, Text } from '@backstage/ui';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { CodeSnippet } from '@backstage/core-components';
import { ImportStepper } from '@backstage/plugin-catalog-import';
import YAML from 'yaml';

/**
 * Props for {@link ImportInfoCard}.
 *
 * @public
 */
export interface ImportInfoCardProps {
  exampleLocationUrl?: string;
  exampleEntity?: Object;
}

/**
 * Shows information about the import process.
 *
 * @public
 */
export const ImportInfoCard = (props: ImportInfoCardProps) => {
  const {
    exampleLocationUrl = 'https://github.com/cariad-dxp/developer-experience-react/blob/main/catalog-info.yaml',
    exampleEntity = {
      apiVersion: 'backstage.io/v1alpha1',
      kind: 'Component',
      metadata: {
        name: 'frobs-management-project',
        description: 'A component for managing the frobs.',
        title: 'Frobs Management Project',
        links: [
          {
            url: 'mailto: cariad-frobs-team@example.com',
            title: 'Contact maintainers',
            icon: 'email',
          },
          {
            url: 'https://frobs-management.example.com',
            title: 'Frobs management portal',
            icon: 'dashboard',
          },
        ],
      },
      spec: {
        owner: 'frobs-team',
        type: 'service',
        lifecycle: 'experimental',
      },
    },
  } = props;

  const configApi = useApi(configApiRef);
  const appTitle = configApi.getOptional('app.title') || 'CARIAD Backstage';
  const [useJson, setUseJson] = useState(false);
  const exampleText: string = useMemo(() => {
    return useJson
      ? JSON.stringify(exampleEntity, undefined, 2)
      : YAML.stringify(exampleEntity, undefined, 2);
  }, [exampleEntity, useJson]);

  return (
    <InfoCard
      title="Register an existing component"
      titleTypographyProps={{ component: 'h3' }}
      deepLink={{
        title: 'Learn more about the CARIAD Software Catalog',
        link: '/catalog/default/Component/xelerate/docs/catalog/',
      }}
    >
      <Typography variant="body2" paragraph>
        <strong>Reminder: Ensure Compliance Before Publishing</strong> <br />
        Before publishing, please ensure that all necessary access restrictions
        are set to comply with confidentiality, eDiscovery, export control, and
        other regulations. For detailed instructions on setting permissions,{' '}
        <a
          target="_blank"
          href="https://developer.cariad.digital/catalog/default/component/xelerate/docs/access_management_basics/"
          style={{ color: '#1EEF97' }}
        >
          click here
        </a>
      </Typography>
      <Typography variant="body2" component="div" mb={2}>
        Enter the URL to your catalog file in your source code repository to add
        it to {appTitle.toString()}.
        <Typography variant="subtitle2">
          <Link to="/catalog/default/component/xelerate/docs/onboarding/">
            How do I create an entity file?
          </Link>
        </Typography>
      </Typography>
      <Typography component="h4" variant="h6">
        Link to an existing entity file
      </Typography>
      <Typography variant="subtitle2" color="textSecondary" paragraph>
        Example: <code>{exampleLocationUrl}</code>
      </Typography>
      <Typography variant="body2" paragraph>
        The wizard analyzes the file, previews the entities, and adds them to
        the {appTitle.toString()} catalog.
      </Typography>
      <Typography component="h4" variant="h6">
        Create a new entity file
      </Typography>
      <Typography variant="body2" paragraph>
        Create a new .yaml file in your code repository. Copy the sample entity
        declaration and replace the default values with values appropriate to
        your project.
      </Typography>
      <Typography variant="body2" paragraph>
        <>
          YAML
          <Tooltip placement="top" arrow title={`${useJson ? 'JSON' : 'YAML'}`}>
            <Switch
              color="primary"
              checked={useJson}
              onChange={() => setUseJson(!useJson)}
              name="YAML JSON Switch"
              inputProps={{ 'aria-label': 'YAML JSON Switch' }}
            />
          </Tooltip>
          JSON
        </>
      </Typography>

      <div style={{ fontSize: '75%' }} data-testid="code-snippet">
        <CodeSnippet
          text={exampleText}
          language={useJson ? 'json' : 'yaml'}
          showCopyCodeButton
        />
      </div>
    </InfoCard>
  );
};

/**
 * The support button of the catalog import page, shown in its page header.
 *
 * @public
 */
export const CatalogImportSupportButton = () => {
  const appTitle =
    useApi(configApiRef).getOptionalString('app.title') || 'Backstage';

  return (
    <SupportButton>
      Start tracking your component in {appTitle} by adding it to the software
      catalog.
    </SupportButton>
  );
};

/**
 * The default catalog import page. The page header (title and support
 * button) comes from the app page layout.
 *
 * @public
 */
export const CatalogImportPage = () => {
  const theme = useTheme();
  const configApi = useApi(configApiRef);
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const appTitle = configApi.getOptional('app.title') || 'Backstage';

  const contentItems = [
    <Grid item xs={12} md={4} lg={6} xl={8} key="import-info">
      <ImportInfoCard />
    </Grid>,

    <Grid item xs={12} md={8} lg={6} xl={4} key="import-steps">
      <ImportStepper />
    </Grid>,
  ];

  return (
    <Container>
      <Box mb="4">
        <Text as="h2" variant="title-small">
          Start tracking your component in {appTitle.toString()}
        </Text>
      </Box>
      <Grid container spacing={2}>
        {isMobile ? contentItems : contentItems.reverse()}
      </Grid>
    </Container>
  );
};
